import { useState } from 'react';
import { ColorLens as ColorLensIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Modal, Paper, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import ThemeModeControl from 'components/ThemeModeControl';
import { ITheme } from 'theme/types';
import { ColorSelectorProps } from './types';

const ColorSelector = <T extends Record<string, string>>({ config }: ColorSelectorProps<T>) => {
  const theme = useTheme() as unknown as ITheme;
  const {
    colorPalette,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  } = config.useHook();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isModalControlled = config.modalOpen !== undefined && config.onModalOpenChange !== undefined;
  const open = isModalControlled ? Boolean(config.modalOpen) : uncontrolledOpen;
  const setModalOpen = (next: boolean) => {
    if (isModalControlled) {
      config.onModalOpenChange?.(next);
    } else {
      setUncontrolledOpen(next);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    if (config.onClose) {
      config.onClose();
    }
  };

  const onDeletePreset = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeletePreset(name);
  };

  return (
    <>
      {!config.hideTrigger && (
        <Tooltip title={config.tooltip} arrow>
          <IconButton onClick={() => setModalOpen(true)} sx={{ color: 'primary.main', fontSize: 20 }}>
            <ColorLensIcon />
          </IconButton>
        </Tooltip>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 3,
            width: config.modalWidth || '600px',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            ...(config.modalWidth && { margin: '0 auto' }),
          }}
        >
          <Typography variant="h6" color="primary">
            {config.title}
          </Typography>

          {/* Appearance is app-wide; kept apart from this palette's colors and actions. */}
          <ThemeModeControl embedded hint="Applies to the whole app." />

          <Divider />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
                Color palette
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Reset and presets only affect these colors.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                size="small"
                variant="outlined"
                data-testid="color-selector-reset">
                Reset
              </Button>
              <Button
                startIcon={<SaveIcon />}
                onClick={handleOpenSavePreset}
                size="small"
                variant="outlined"
                data-testid="color-selector-save-preset">
                Save preset
              </Button>
            </Box>
          </Box>

          {presets.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Saved presets
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {presets.map(preset => (
                  <Chip
                    key={preset.name}
                    label={preset.name}
                    size="small"
                    variant="outlined"
                    onClick={() => handleApplyPreset(preset.name)}
                    onDelete={e => onDeletePreset(preset.name, e)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                    data-testid={`color-selector-preset-${preset.name}`}
                  />
                ))}
              </Box>
            </Box>
          )}

          {config.sections.map(section => (
            <Box key={section.title}>
              <Typography variant="overline" color="text.secondary" component="div" sx={{ mb: 1, lineHeight: 1.5 }}>
                {section.title}
              </Typography>
              <Stack spacing={1}>
                {section.keys.map(key => {
                  const swatchColor = colorPalette[key] ?? theme.palette.white ?? '#FFFFFF';
                  const swatchTextColor = theme.palette.getContrastText(swatchColor);

                  return (
                    <Box
                      key={String(key)}
                      sx={{ display: 'grid', gridTemplateColumns: '1fr 56px 80px', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          minHeight: 36,
                          px: 1.5,
                          backgroundColor: swatchColor,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                        <Typography variant="caption" sx={{ color: swatchTextColor, fontWeight: 600 }}>
                          {config.statusLabels[key]}
                        </Typography>
                      </Box>
                      <TextField
                        type="color"
                        value={config.toSolidColor(swatchColor)}
                        onChange={e => handleColorChange(key, e.target.value)}
                        size="small"
                        slotProps={{
                          htmlInput: {
                            'aria-label': `${config.statusLabels[key]} color`,
                            'data-testid': `color-selector-input-${String(key)}`,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {swatchColor.toUpperCase()}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
          {config.showPreview && config.previewComponent && (
            <Box>
              <Typography variant="overline" color="text.secondary" component="div" sx={{ mb: 1, lineHeight: 1.5 }}>
                Preview
              </Typography>
              {config.previewComponent(colorPalette)}
            </Box>
          )}

          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose} variant="contained" data-testid="color-selector-done">
              Done
            </Button>
          </Box>
        </Paper>
      </Modal>
      <Dialog open={savePresetOpen} onClose={handleCloseSavePreset}>
        <DialogTitle>Save Color Palette Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Preset Name"
            fullWidth
            variant="outlined"
            value={presetName}
            onChange={e => setPresetName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && presetName.trim()) {
                handleSavePreset();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSavePreset}>Cancel</Button>
          <Button onClick={handleSavePreset} variant="contained" disabled={!presetName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ColorSelector;

